import React, { JSX, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { ReactComponent as ChevronDown } from '../icons/chevron_down.svg';
import { Button, ButtonData } from '../jsxElements';
import styles from './GenericCard.module.css';
import CareInfoDialog from './CareCards/CareInfoDialog';
import generalConfig from '../resources/general.json';
import { ReactComponent as InfoSvg } from '../icons/info.svg';

import { AmpFlagSet } from '../amplitude';

const MAX_QUANTITY_BEFORE_COLLAPSE = 2;

export enum GenericContentType {
    Info,
    Link,
    Disclaimer,
    Quantity,
}

export interface GenericCardData {
    title: GenericCardTitle;
    subtitles?: GenericCardSubtitle[];
    headerImageUrl?: string;
    content: GenericCardContent[];
    buttonData?: ButtonData;
}

export interface GenericCardTitle {
    text: string;
    disableTruncation?: boolean;
}

export interface GenericCardSubtitle {
    text: string;
    isPhoneNumber?: boolean;
    newTab?: boolean;
    phoneNumberNumerical?: string;
    onClick?: React.MouseEventHandler<HTMLAnchorElement>;
    disableTruncation?: boolean;
    imageUrl?: string;
    image?: JSX.Element;
    instruction?: string;
}

export type GenericCardContent =
    | GenericCardInfo
    | GenericCardLink
    | GenericCardDisclaimer
    | GenericCardQuantity;

export interface GenericCardInfo {
    type: GenericContentType;
    title?: GenericCardTitle;
    info: GenericCardSubtitle;
    imageUrl?: string;
    accordionInfo?: JSX.Element;
    image?: JSX.Element;
}

export interface GenericCardLink {
    type: GenericContentType;
    title: string;
    linkUrl: string;
    imageUrl?: string;
    image?: JSX.Element;
}

export interface GenericCardDisclaimer {
    type: GenericContentType;
    disclaimer: string;
}

export interface GenericCardQuantity {
    type: GenericContentType;
    title: GenericCardTitle;
    secondTitleLine?: GenericCardTitle;
    details: GenericCardSubtitle;
    quantity: number;
    quantityEmphasizeZero?: boolean;
    label: string;
    onClick?: React.MouseEventHandler<HTMLDivElement>;
}

function isTextTruncated(currentRef: HTMLDivElement): boolean {
    return (
        currentRef.scrollHeight > currentRef.clientHeight ||
        currentRef.scrollWidth > currentRef.clientWidth
    );
}

const ExpandableTextLabel: React.FC<{
    className: string;
    text: string;
    shouldExpand: boolean;
    lineLimit: number;
    setShowSeeMore: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ className, text, shouldExpand, lineLimit, setShowSeeMore }) => {
    const ref = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (ref.current && isTextTruncated(ref.current)) {
            setShowSeeMore(true);
        }
    }, [ref, setShowSeeMore]);
    return (
        <div
            ref={ref}
            className={className}
            style={
                shouldExpand
                    ? undefined
                    : { lineClamp: lineLimit, WebkitLineClamp: lineLimit }
            }
        >
            {text}
        </div>
    );
};

const Header: React.FC<{
    data: GenericCardData;
}> = ({ data }) => {
    const [shouldExpand, setShouldExpand] = useState(false);
    const [showSeeMore, setShowSeeMore] = useState(false);
    const title = data.title.disableTruncation ? (
        <div className={styles.genericCardTitle}>{data.title.text}</div>
    ) : (
        <ExpandableTextLabel
            className={styles.genericCardTitle}
            text={data.title.text}
            shouldExpand={shouldExpand}
            lineLimit={1}
            setShowSeeMore={setShowSeeMore}
        />
    );
    const subtitles =
        data.subtitles && data.subtitles.length > 0
            ? data.subtitles.map((subtitle, idx) => {
                  if (subtitle.isPhoneNumber) {
                      return (
                          <a
                              key={`subtitle-${idx}`}
                              className={styles.genericCardLinkText}
                              href={'tel:' + subtitle.phoneNumberNumerical}
                              target={subtitle.newTab ? '_blank' : '_self'}
                              onClick={subtitle.onClick}
                          >
                              {subtitle.text}
                          </a>
                      );
                  } else if (subtitle.disableTruncation) {
                      return (
                          <div
                              key={`subtitle-${idx}`}
                              className={styles.genericCardSubtitle}
                          >
                              {subtitle.text}
                          </div>
                      );
                  }
                  return (
                      <ExpandableTextLabel
                          key={`subtitle-${idx}`}
                          className={styles.genericCardSubtitle}
                          text={subtitle.text}
                          shouldExpand={shouldExpand}
                          lineLimit={1}
                          setShowSeeMore={setShowSeeMore}
                      />
                  );
              })
            : null;

    return (
        <div className={styles.genericCardHeader}>
            {data.headerImageUrl && (
                <div className={styles.genericCardHeaderImage}>
                    <img src={data.headerImageUrl} alt={'header'} />
                </div>
            )}
            <div className={styles.genericCardHeaderText}>
                {title}
                <div className={styles.genericCardSeeMoreContainer}>
                    {subtitles && (
                        <div className={styles.genericCardSubtitlesContainer}>
                            {subtitles}
                        </div>
                    )}
                    {showSeeMore && (
                        <div
                            className={styles.genericCardSeeMore}
                            onClick={() => setShouldExpand(!shouldExpand)}
                        >
                            {shouldExpand
                                ? generalConfig.providence.genericCardSeeLess
                                : generalConfig.providence.genericCardSeeMore}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const ContentInfo: React.FC<{
    item: GenericCardInfo;
    openDialog: boolean;
    handleAccordionToggle: () => void;
}> = ({ item, openDialog, handleAccordionToggle }) => {
    const [shouldExpand, setShouldExpand] = useState(false);
    const [showSeeMore, setShowSeeMore] = useState(false);
    const title = !item.title ? null : item.title.disableTruncation ? (
        <div className={styles.genericCardContentTitle}>{item.title.text}</div>
    ) : (
        <ExpandableTextLabel
            className={styles.genericCardContentTitle}
            text={item.title.text}
            shouldExpand={shouldExpand}
            lineLimit={2}
            setShowSeeMore={setShowSeeMore}
        />
    );
    const subtitle =
        item.info.text === 'unknown' ? null : item.info.disableTruncation ? (
            <div className={styles.genericCardContentInfo}>
                {item.info.text}
            </div>
        ) : (
            <ExpandableTextLabel
                className={styles.genericCardContentInfo}
                text={item.info.text}
                shouldExpand={shouldExpand}
                lineLimit={2}
                setShowSeeMore={setShowSeeMore}
            />
        );
    return (
        <div className={styles.genericCardContent}>
            {item.imageUrl && (
                <div className={styles.genericCardContentImage}>
                    <img src={item.imageUrl} alt={'content-info'} />
                </div>
            )}
            {item.image && item.accordionInfo ? (
                <>
                    <div
                        className={styles.genericCardContentImage}
                        onClick={handleAccordionToggle}
                    >
                        {item.image}
                    </div>
                    <CareInfoDialog
                        isOpen={openDialog}
                        handleToggle={handleAccordionToggle}
                        dialogContent={item.accordionInfo}
                    />
                </>
            ) : (
                item.image && (
                    <div className={styles.genericCardContentImage}>
                        {item.image}
                    </div>
                )
            )}
            <div className={styles.genericCardContentText}>
                {title}
                <div className={styles.genericCardSeeMoreContainer}>
                    <div className={styles.genericCardSubtitlesContainer}>
                        {subtitle}
                    </div>
                    {showSeeMore && (
                        <div
                            className={styles.genericCardSeeMore}
                            onClick={() => setShouldExpand(!shouldExpand)}
                        >
                            {shouldExpand
                                ? generalConfig.providence.genericCardSeeLess
                                : generalConfig.providence.genericCardSeeMore}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const ContentLink: React.FC<{
    item: GenericCardLink;
}> = ({ item }) => (
    <div className={styles.genericCardContent}>
        {item.imageUrl && (
            <div className={styles.genericCardContentImage}>
                <img src={item.imageUrl} alt={'content-link'} />
            </div>
        )}
        {item.image && (
            <div className={styles.genericCardContentImage}>{item.image}</div>
        )}
        <a
            className={styles.genericCardLinkText}
            href={item.linkUrl}
            rel="noopener noreferrer nofollow"
            target="_blank"
        >
            {item.title}
        </a>
    </div>
);

const ContentDisclaimer: React.FC<{
    item: GenericCardDisclaimer;
}> = ({ item }) => (
    <div className={styles.genericCardContent}>
        <div className={styles.genericCardContentImage}>
            {<InfoSvg className={styles.clinicCardContentIcon} />}
        </div>
        <div className={styles.genericCardDisclaimerText}>
            {item.disclaimer}
        </div>
    </div>
);

const ContentQuantity: React.FC<{
    item: GenericCardQuantity;
}> = ({ item }) => {
    const [shouldExpand, setShouldExpand] = useState(false);
    const [showSeeMore, setShowSeeMore] = useState(false);
    const title = item.title.disableTruncation ? (
        <div className={styles.genericCardValueTitle}>{item.title.text}</div>
    ) : (
        <ExpandableTextLabel
            className={styles.genericCardValueTitle}
            text={item.title.text}
            shouldExpand={shouldExpand}
            lineLimit={2}
            setShowSeeMore={setShowSeeMore}
        />
    );
    const secondTitleLine = item.secondTitleLine && (
        <div className={styles.genericCardValueSecondTitle}>
            {item.secondTitleLine.text}
        </div>
    );

    const instruction = item.details.instruction && (
        <div className={styles.genericCardInstructionContainer}>
            {item.details.instruction}
        </div>
    );

    let subtitle;
    if (item.details.isPhoneNumber) {
        subtitle = (
            <a
                className={styles.genericCardPhoneLink}
                href={'tel:' + item.details.phoneNumberNumerical}
                target={item.details.newTab ? '_blank' : '_self'}
                onClick={item.details.onClick}
            >
                {item.details.imageUrl && (
                    <div className={styles.genericCardPhoneImage}>
                        <img src={item.details.imageUrl} alt={'icon'} />
                        <div className={styles.genericCardPhoneLinkText}>
                            {item.details.text}
                        </div>
                    </div>
                )}
                {item.details.image && (
                    <div className={styles.genericCardPhoneImage}>
                        {item.details.image}
                        <div className={styles.genericCardPhoneLinkText}>
                            {item.details.text}
                        </div>
                    </div>
                )}
            </a>
        );
    } else {
        subtitle = item.details.disableTruncation ? (
            <div className={styles.genericCardValueInfo}>
                {item.details.text}
            </div>
        ) : (
            <ExpandableTextLabel
                className={styles.genericCardValueInfo}
                text={item.details.text}
                shouldExpand={shouldExpand}
                lineLimit={2}
                setShowSeeMore={setShowSeeMore}
            />
        );
    }
    let quantityZeroClass = null;
    if (item.quantity === 0 && item.quantityEmphasizeZero) {
        quantityZeroClass = styles.genericCardValueQuantityZero;
    }
    return (
        <div
            className={
                item.onClick
                    ? styles.genericClickableCardValueContainer
                    : styles.genericCardValueContainer
            }
            onClick={item.onClick}
        >
            <div className={styles.genericCardValueText}>
                {title}
                {secondTitleLine}
                <div className={styles.genericCardSeeMoreContainer}>
                    <div>
                        <div className={styles.genericCardSubtitlesContainer}>
                            {subtitle}
                        </div>
                        {instruction}
                    </div>
                    {showSeeMore && (
                        <div
                            className={styles.genericCardSeeMore}
                            onClick={(event) => {
                                event.stopPropagation();
                                setShouldExpand(!shouldExpand);
                            }}
                        >
                            {shouldExpand
                                ? generalConfig.providence.genericCardSeeLess
                                : generalConfig.providence.genericCardSeeMore}
                        </div>
                    )}
                </div>
            </div>
            <div className={styles.genericCardValueQuantity}>
                <div
                    className={classNames(
                        styles.genericCardValueNumber,
                        quantityZeroClass
                    )}
                >
                    {item.quantity}
                </div>
                <div
                    className={classNames(
                        styles.genericCardValueLabel,
                        quantityZeroClass
                    )}
                >
                    {item.label}
                </div>
            </div>
        </div>
    );
};

const Content: React.FC<{
    data: GenericCardData;
}> = ({ data }) => {
    const [viewAll, setViewAll] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const handleAccordionToggle: () => void = () => {
        setOpenDialog(!openDialog);
    };
    const content: JSX.Element[] = [];
    let showViewAll = false;
    let quantityCount = 0;
    for (let i = 0; i < data.content.length; i++) {
        const item = data.content[i];
        switch (item.type) {
            case GenericContentType.Info:
                content.push(
                    <ContentInfo
                        key={`content-info-${i}`}
                        item={item as GenericCardInfo}
                        openDialog={openDialog}
                        handleAccordionToggle={handleAccordionToggle}
                    />
                );
                break;
            case GenericContentType.Link:
                content.push(
                    <ContentLink
                        key={`content-link-${i}`}
                        item={item as GenericCardLink}
                    />
                );
                break;
            case GenericContentType.Disclaimer:
                content.push(
                    <ContentDisclaimer
                        key={`content-disclaimer-${i}`}
                        item={item as GenericCardDisclaimer}
                    />
                );
                break;
            case GenericContentType.Quantity:
                quantityCount++;
                if (quantityCount > MAX_QUANTITY_BEFORE_COLLAPSE && !viewAll) {
                    if (!showViewAll) showViewAll = true;
                    break;
                }
                content.push(
                    <ContentQuantity
                        key={`content-quantity-${i}`}
                        item={item as GenericCardQuantity}
                    />
                );
                break;
            default: {
                // Do not add content if type doesn't match enum
            }
        }
    }
    return (
        <div>
            {content}
            {showViewAll && !viewAll && (
                <div
                    className={styles.genericCardViewAll}
                    onClick={() => setViewAll(true)}
                >
                    <div className={styles.genericCardViewAllText}>
                        {generalConfig.providence.genericCardViewAll}
                    </div>
                    <ChevronDown className={styles.genericCardContentIcon} />
                </div>
            )}
        </div>
    );
};

const GenericCard: React.FC<{
    data: GenericCardData;
    featureFlags: AmpFlagSet;
}> = ({ data, featureFlags }) => {
    return (
        <div className={styles.genericCardContainer}>
            <div>
                <Header data={data} />
                <Content data={data} />
            </div>
            {data.buttonData && (
                <Button
                    className={styles.genericCardButtonContainer}
                    button={data.buttonData}
                    disableButtons={false}
                    featureFlags={featureFlags}
                />
            )}
        </div>
    );
};

export default GenericCard;
